import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import PartnerShell, { PartnerNotice, formatDay, formatVisit } from "./PartnerShell";
import { Search, Loader2 } from "lucide-react";

const TAB_ORDER = ["needs_scheduling", "scheduled", "seen", "closed"];

export default function PartnerPatients() {
  const [tab, setTab] = useState("needs_scheduling");
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const params = new URLSearchParams({ tab, page: String(page) });
      if (applied) params.set("q", applied);
      const res = await api.get(`/partner/patients?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      setStatus(err?.response?.status || 500);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tab, page, applied]);

  useEffect(() => { load(); }, [load]);

  const tabs = data?.tabs || TAB_ORDER.map((t) => ({ tab: t, label: t, count: 0 }));
  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50))) : 1;

  return (
    <PartnerShell>
      <h1 className="mb-4 text-xl font-semibold text-slate-900">My patients</h1>
      {status ? <PartnerNotice status={status} /> : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.tab}
                type="button"
                role="tab"
                aria-selected={tab === t.tab}
                data-testid={`partner-tab-${t.tab}`}
                onClick={() => { setTab(t.tab); setPage(1); }}
                className={`rounded-full px-3 py-1.5 text-sm ${tab === t.tab ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"}`}
              >
                {t.label} <span className="ml-1 opacity-75">{t.count}</span>
              </button>
            ))}
          </div>
          <div className="mb-4 flex max-w-md items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <input
                data-testid="partner-search"
                value={query}
                maxLength={100}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setApplied(query.trim()); setPage(1); } }}
                placeholder="Search by patient name"
                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-2 text-sm"
              />
            </div>
            <button
              type="button"
              data-testid="partner-search-go"
              onClick={() => { setApplied(query.trim()); setPage(1); }}
              className="rounded-md bg-slate-900 px-3 py-2 text-sm text-white"
            >
              Search
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Patient</th>
                  <th className="px-3 py-2 font-medium">Date of birth</th>
                  <th className="px-3 py-2 font-medium">Referred</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Next visit</th>
                  <th className="px-3 py-2 font-medium">Clinic</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…
                  </td></tr>
                ) : (data?.items || []).length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500" data-testid="partner-empty">
                    No patients here.
                  </td></tr>
                ) : data.items.map((p) => (
                  <tr key={p.ref} className="border-t border-slate-100" data-testid="partner-row">
                    <td className="px-3 py-2">
                      <Link to={`/partner/patients/${p.ref}`} className="font-medium text-blue-700 hover:underline">
                        {p.last_name}, {p.first_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{formatDay(p.date_of_birth)}</td>
                    <td className="px-3 py-2">{formatDay(p.referral_date)}</td>
                    <td className="px-3 py-2">{p.tab_label}</td>
                    <td className="px-3 py-2">{formatVisit(p.next_visit)}</td>
                    <td className="px-3 py-2">{p.clinic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}
    </PartnerShell>
  );
}
